import { useState, useEffect } from 'react';
import {
  makeStyles,
  tokens,
  Card,
  Title2,
  Body1,
  Field,
  Input,
  Textarea,
  Dropdown,
  Option,
  Button,
  Combobox,
  Persona,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Tag,
  TagGroup,
} from '@fluentui/react-components';
import { PeopleTeamRegular, CheckmarkCircleRegular } from '@fluentui/react-icons';
import { apiClient, ListResponse } from '../api/client';
import { TEAM_VISIBILITY, DATA_LOCATIONS } from '../config/constants';

const useStyles = makeStyles({
  container: {
    maxWidth: '800px',
    margin: '0 auto',
  },
  header: {
    marginBottom: tokens.spacingVerticalXL,
  },
  card: {
    padding: tokens.spacingVerticalXL,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: tokens.spacingHorizontalL,
  },
  emailRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: tokens.spacingHorizontalS,
  },
  emailSuffix: {
    padding: `${tokens.spacingVerticalM} 0`,
    color: tokens.colorNeutralForeground3,
  },
  memberTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalS,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalL,
    paddingTop: tokens.spacingVerticalL,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  successCard: {
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
  },
  successIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: '#e6f4ea',
    color: '#137333',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
    marginBottom: tokens.spacingVerticalL,
    fontSize: '32px',
  },
});

interface GraphUser {
  id: string;
  displayName: string;
  mail: string;
}

interface FormData {
  displayName: string;
  description: string;
  visibility: 'private' | 'public';
  mailNickname: string;
  owners: GraphUser[];
  members: GraphUser[];
  preferredDataLocation: string;
}

const initialFormData: FormData = {
  displayName: '',
  description: '',
  visibility: 'private',
  mailNickname: '',
  owners: [],
  members: [],
  preferredDataLocation: 'EUR',
};

export function TeamsPage() {
  const styles = useStyles();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [memberQuery, setMemberQuery] = useState('');
  const [searchResults, setSearchResults] = useState<GraphUser[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [result, setResult] = useState<{ webUrl?: string; displayName?: string } | null>(null);

  // Search users
  useEffect(() => {
    if (memberQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const response = await apiClient.get<ListResponse<GraphUser>>(
          `/users/search?q=${encodeURIComponent(memberQuery)}`
        );
        // Filter out already selected members
        const selectedIds = [...formData.owners, ...formData.members].map(u => u.id);
        setSearchResults(response.data.filter(u => !selectedIds.includes(u.id)));
      } catch (error) {
        console.error('Error searching users:', error);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [memberQuery, formData.owners, formData.members]);

  const handleAddOwner = (user: GraphUser) => {
    setFormData({ ...formData, owners: [...formData.owners, user] });
    setMemberQuery('');
  };

  const handleAddMember = (user: GraphUser) => {
    setFormData({ ...formData, members: [...formData.members, user] });
    setMemberQuery('');
  };

  const handleRemoveOwner = (userId: string) => {
    setFormData({ ...formData, owners: formData.owners.filter(u => u.id !== userId) });
  };

  const handleRemoveMember = (userId: string) => {
    setFormData({ ...formData, members: formData.members.filter(u => u.id !== userId) });
  };

  const handleSubmit = async () => {
    if (!formData.displayName || !formData.mailNickname || formData.members.length === 0) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.owners.length === 0) {
      setError('At least one owner is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await apiClient.post<{ data: { webUrl?: string; displayName?: string } }>('/teams/create', {
        displayName: formData.displayName,
        description: formData.description,
        visibility: formData.visibility,
        mailNickname: formData.mailNickname,
        owners: formData.owners.map(u => u.id),
        members: formData.members.map(u => u.id),
        preferredDataLocation: formData.preferredDataLocation,
      });

      setResult(response.data);
      setIsComplete(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create Teams site');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setIsComplete(false);
    setResult(null);
  };

  if (isComplete) {
    return (
      <div className={styles.container}>
        <Card className={styles.successCard}>
          <div className={styles.successIcon}>
            <CheckmarkCircleRegular />
          </div>
          <Title2 style={{ marginBottom: tokens.spacingVerticalM }}>
            Teams Site Created!
          </Title2>
          <Body1 style={{ color: tokens.colorNeutralForeground3, marginBottom: tokens.spacingVerticalL }}>
            {result?.displayName} has been created successfully.
          </Body1>
          {result?.webUrl && (
            <MessageBar intent="success" style={{ marginBottom: tokens.spacingVerticalL }}>
              <MessageBarBody>
                <MessageBarTitle>Teams URL</MessageBarTitle>
                <a href={result.webUrl} target="_blank" rel="noopener noreferrer">
                  {result.webUrl}
                </a>
              </MessageBarBody>
            </MessageBar>
          )}
          <Button appearance="primary" onClick={handleReset}>
            Create Another Team
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title2>Create Teams Site</Title2>
        <Body1 style={{ color: tokens.colorNeutralForeground3 }}>
          Set up a new Microsoft Teams site for your team or project
        </Body1>
      </div>

      <Card className={styles.card}>
        {error && (
          <MessageBar intent="error" style={{ marginBottom: tokens.spacingVerticalL }}>
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        <div className={styles.form}>
          <Field label="Team Name" required>
            <Input
              value={formData.displayName}
              onChange={(_, data) => setFormData({ ...formData, displayName: data.value })}
              placeholder="Enter team name"
            />
          </Field>

          <Field label="Description">
            <Textarea
              value={formData.description}
              onChange={(_, data) => setFormData({ ...formData, description: data.value })}
              placeholder="Describe this team..."
              rows={3}
            />
          </Field>

          <div className={styles.row}>
            <Field label="Visibility" required>
              <Dropdown
                value={formData.visibility === 'private' ? 'Private' : 'Public'}
                onOptionSelect={(_, data) =>
                  setFormData({ ...formData, visibility: data.optionValue as 'private' | 'public' })
                }
              >
                {TEAM_VISIBILITY.map((v) => (
                  <Option key={v.key} value={v.key} text={v.text}>
                    {v.text}
                  </Option>
                ))}
              </Dropdown>
            </Field>

            <Field label="Data Location" required>
              <Dropdown
                value={DATA_LOCATIONS.find(l => l.key === formData.preferredDataLocation)?.text || ''}
                onOptionSelect={(_, data) =>
                  setFormData({ ...formData, preferredDataLocation: data.optionValue as string })
                }
              >
                {DATA_LOCATIONS.map((loc) => (
                  <Option key={loc.key} value={loc.key} text={loc.text}>
                    {loc.text}
                  </Option>
                ))}
              </Dropdown>
            </Field>
          </div>

          <Field label="Team Email Address" required>
            <div className={styles.emailRow}>
              <Input
                value={formData.mailNickname}
                onChange={(_, data) =>
                  setFormData({ ...formData, mailNickname: data.value.replace(/[^a-zA-Z0-9-]/g, '') })
                }
                placeholder="team-name"
                style={{ flex: 1 }}
              />
              <span className={styles.emailSuffix}>@nitrexhts.com</span>
            </div>
          </Field>

          <Field label="Owners" required hint="At least one owner is required">
            <Combobox
              value={memberQuery}
              onChange={(e) => setMemberQuery((e.target as HTMLInputElement).value)}
              placeholder="Search for owners..."
              freeform
            >
              {searchLoading && (
                <Option value="" text="Searching..." disabled>
                  <Spinner size="tiny" /> Searching...
                </Option>
              )}
              {searchResults.map((user) => (
                <Option
                  key={user.id}
                  value={user.id}
                  text={user.displayName}
                  onClick={() => handleAddOwner(user)}
                >
                  <Persona name={user.displayName} secondaryText={user.mail} size="small" />
                </Option>
              ))}
            </Combobox>
            {formData.owners.length > 0 && (
              <TagGroup className={styles.memberTags}>
                {formData.owners.map((user) => (
                  <Tag
                    key={user.id}
                    dismissible
                    dismissIcon={{ "aria-label": "remove" }}
                    value={user.id}
                    onClick={() => handleRemoveOwner(user.id)}
                  >
                    {user.displayName}
                  </Tag>
                ))}
              </TagGroup>
            )}
          </Field>

          <Field label="Members" required hint="At least one member is required">
            <Combobox
              value={memberQuery}
              onChange={(e) => setMemberQuery((e.target as HTMLInputElement).value)}
              placeholder="Search for members..."
              freeform
            >
              {searchLoading && (
                <Option value="" text="Searching..." disabled>
                  <Spinner size="tiny" /> Searching...
                </Option>
              )}
              {searchResults.map((user) => (
                <Option
                  key={user.id}
                  value={user.id}
                  text={user.displayName}
                  onClick={() => handleAddMember(user)}
                >
                  <Persona name={user.displayName} secondaryText={user.mail} size="small" />
                </Option>
              ))}
            </Combobox>
            {formData.members.length > 0 && (
              <TagGroup className={styles.memberTags}>
                {formData.members.map((user) => (
                  <Tag
                    key={user.id}
                    dismissible
                    dismissIcon={{ "aria-label": "remove" }}
                    value={user.id}
                    onClick={() => handleRemoveMember(user.id)}
                  >
                    {user.displayName}
                  </Tag>
                ))}
              </TagGroup>
            )}
          </Field>

          <div className={styles.actions}>
            <Button appearance="secondary" onClick={handleReset}>
              Clear
            </Button>
            <Button
              appearance="primary"
              icon={isSubmitting ? <Spinner size="tiny" /> : <PeopleTeamRegular />}
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Team'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
